// components/company/tabs/CompanyDocuments.jsx
// UPDATED: Recommended doc kinds + Show all toggle + Waiver support + Smart fields
import { useState, Fragment, useMemo, useRef, useEffect } from "react";
import { Field } from "../../ui/Field";
import {
  addCompanyDoc,
  updateCompanyDoc,
  deleteCompanyDoc,
  downloadCompanyDoc,
  getCompanyDocSignedUrl,
  openDocumentWithSignedUrl,
  getCompanyWaivers,
  createCompanyWaiver,
  deleteCompanyWaiver,
} from "../../../api";
import { RequirementsStatusCard } from "../RequirementsStatusCard";
import ErrorBoundary from "../../ErrorBoundary";

// ✅ IMPORTANT: frontend source of truth
import { DOC_KINDS, getVisibleFields, getDocKindsByCategory } from "../../../docKinds";

// Autocomplete for recent custom documents
const RECENT_CUSTOM_DOCS_KEY = "cbcfx_recent_custom_docs";
function getRecentCustomDocs() {
  try {
    const stored = localStorage.getItem(RECENT_CUSTOM_DOCS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}
function saveRecentCustomDoc(label) {
  try {
    const recent = getRecentCustomDocs();
    const updated = [label, ...recent.filter((d) => d !== label)].slice(0, 10);
    localStorage.setItem(RECENT_CUSTOM_DOCS_KEY, JSON.stringify(updated));
  } catch {}
}

/**
 * "Google/AWS-style" default shortlist:
 * - High-signal docs used constantly
 * - Everything else stays available behind "Show all"
 */
const RECOMMENDED_KIND_KEYS = [
  // people
  "passport",
  "id",

  // company core
  "coc_extract",
  "business_license",

  // financial core
  "bank_statement",
  "financial_statement",

  // compliance backbone
  "kyc_form",

  // flexible escape hatch
  "custom",
];

function titleCase(s) {
  return String(s || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

const prettyKind = (k, identifier) => {
  if (/^CoC Fee Receipt/i.test(String(identifier || ""))) return "CoC receipt";
  if (!k) return "—";

  // Legacy mappings (keep for existing stored values / backwards compatibility)
  const up = String(k).toUpperCase();
  if (up === "VESTIGINGS_VERGUNNING") return "Vestigingsvergunning";
  if (up === "DEVIEZEN_VRIJSTELLING") return "Deviezenvrijstelling";
  if (up === "CRIB_NUMMER") return "CRIB nummer";
  if (up === "FINANCIAL_STATEMENTS") return "Financial Statements";
  if (up === "COC_FORM") return "CoC form";
  if (up === "BANK_STATEMENT") return "Bank statement";

  const low = String(k).toLowerCase();
  if (low === "passport") return "Passport";
  if (low === "id") return "National ID";
  if (low === "other") return "Other";

  // Try to find in DOC_KINDS
  const match = DOC_KINDS.find((d) => d.key === low);
  if (match) return match.label;

  return String(k)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const autoExpiry = (kindSlug, issued) => {
  if (!issued) return "";
  const d = new Date(issued);
  if (isNaN(d)) return "";
  const k = String(kindSlug || "").toLowerCase();

  // Keep existing behavior (safe)
  if (k === "passport") d.setFullYear(d.getFullYear() + 10);
  else if (k === "id") d.setFullYear(d.getFullYear() + 10);
  else return "";

  return d.toISOString().slice(0, 10);
};

function kindMatchesScopeTarget(kindDef, scope) {
  // scope is "company" or "ubo"
  // ubo = person target
  const target = String(kindDef?.target || "").toLowerCase();
  if (scope === "ubo") return target === "person" || target === "both";
  return target === "company" || target === "both";
}

export const CompanyDocuments = ({ docs = [], requirements = [], roles = [], company, companyId, meta, notify, refetch }) => {
  const [editDoc, setEditDoc] = useState(null);
  const [newDoc, setNewDoc] = useState({
    scope: "company",
    kind: "",
    kind_label: "",
    other_kind: "",
    identifier: "",
    issued_on: new Date().toISOString().slice(0, 10),
    expires_on: "",
    period_from: "",
    period_to: "",
    authority: "",
    note: "",
    file: null,
    person_id: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Custom document state
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customSuggestions, setCustomSuggestions] = useState([]);

  // ✅ Recommended vs Show all toggle
  const [showAllKinds, setShowAllKinds] = useState(false);

  // Waiver state
  const [waivers, setWaivers] = useState([]);
  const [waiverModal, setWaiverModal] = useState(null);
  const [waiverReason, setWaiverReason] = useState("");
  const [waiverLoading, setWaiverLoading] = useState(false);

  const uploadFormRef = useRef(null);

  const residency = (company?.type_local_foreign || company?.residency_status || "").toLowerCase();
  const isLocal = residency === "local";
  const isIntl = residency === "international" || residency === "foreign";

  const uboOptions = useMemo(
    () =>
      (roles || [])
        .filter(
          (r) =>
            r &&
            String(r.role || "")
              .toLowerCase()
              .includes("ubo") &&
            (r.person_id || r.person?.id),
        )
        .map((r) => ({
          id: r.person_id || r.person?.id,
          name: r.person_name || r.person?.full_name || [r.first_name || r.person?.first_name, r.last_name || r.person?.last_name].filter(Boolean).join(" "),
        })),
    [roles],
  );

  // Get visible fields for current document kind
  const visibleFields = useMemo(() => {
    const kind = newDoc.kind || "custom";
    return getVisibleFields(kind);
  }, [newDoc.kind]);

  // Load waivers on mount
  useEffect(() => {
    loadWaivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadWaivers = async () => {
    try {
      const data = await getCompanyWaivers(companyId);
      setWaivers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[CompanyDocuments] Failed to load waivers:", error);
      setWaivers([]);
    }
  };

  const handleWaiverRequest = (doc) => {
    setWaiverModal({
      doc_kind: doc.kind,
      label: doc.label,
    });
    setWaiverReason("");
  };

  const handleWaiverSubmit = async (e) => {
    e.preventDefault();

    if (!waiverReason.trim() || waiverReason.trim().length < 10) {
      notify("Reason must be at least 10 characters");
      return;
    }

    setWaiverLoading(true);
    try {
      await createCompanyWaiver(companyId, {
        doc_kind: waiverModal.doc_kind,
        reason: waiverReason.trim(),
      });

      await loadWaivers();
      await refetch();

      notify(`${waiverModal.label} marked as not applicable`);
      setWaiverModal(null);
      setWaiverReason("");
    } catch (error) {
      console.error("[CompanyDocuments] Waiver creation failed:", error);
      notify(error.message || "Failed to create waiver");
    } finally {
      setWaiverLoading(false);
    }
  };

  const handleWaiverDelete = async (waiverId) => {
    if (!confirm("Remove this waiver? The document will be required again.")) {
      return;
    }

    try {
      await deleteCompanyWaiver(companyId, waiverId);
      await loadWaivers();
      await refetch();
      notify("Waiver removed");
    } catch (error) {
      console.error("[CompanyDocuments] Waiver deletion failed:", error);
      notify(error.message || "Failed to remove waiver");
    }
  };

  const validateNewDoc = () => {
    const errs = {};
    if (!newDoc.kind) errs.kind = "Document kind is required.";
    if (newDoc.kind === "custom" && !newDoc.kind_label?.trim()) {
      errs.kind_label = "Document name is required for custom documents.";
    }
    if (newDoc.kind_label && newDoc.kind_label.length > 100) {
      errs.kind_label = "Document name must be less than 100 characters.";
    }
    if (newDoc.scope === "ubo" && !newDoc.person_id) errs.person_id = "Please select a UBO for this document.";
    return errs;
  };

  const handleAddDoc = async (e) => {
    e.preventDefault();
    const errs = validateNewDoc();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      // Save custom document to recent
      if (newDoc.kind === "custom" && newDoc.kind_label) {
        saveRecentCustomDoc(newDoc.kind_label);
      }

      await addCompanyDoc(companyId, newDoc, newDoc.scope === "ubo" ? newDoc.person_id : undefined);

      setNewDoc({
        scope: "company",
        kind: "",
        kind_label: "",
        other_kind: "",
        identifier: "",
        issued_on: "",
        expires_on: "",
        period_from: "",
        period_to: "",
        authority: "",
        note: "",
        file: null,
        person_id: "",
      });
      setShowCustomInput(false);
      setErrors({});
      await refetch();
      notify("Document added successfully");
    } catch (err) {
      notify(err?.message || "Failed to add document");
    } finally {
      setLoading(false);
    }
  };

  const startEditDocRow = (doc) => {
    const raw = doc.kind || "";

    const isCompanyKind = ["VESTIGINGS_VERGUNNING", "DEVIEZEN_VRIJSTELLING", "CRIB_NUMMER", "FINANCIAL_STATEMENTS"].includes(String(raw).toUpperCase());

    const normalized = isCompanyKind ? String(raw).toUpperCase() : String(raw).toLowerCase();

    setShowAllKinds(true); // ← ensure the full list renders so the current kind is selectable
    setEditDoc({
      id: doc.id,
      kind: normalized,
      other_kind: "",
      identifier: doc.identifier || "",
      issued_on: doc.issued_on ? doc.issued_on.slice(0, 10) : "",
      expires_on: doc.expires_on ? doc.expires_on.slice(0, 10) : "",
      status: doc.status || "",
      note: doc.note || "",
      file: null,
      path: doc.path || null,
    });
  };

  const saveDocEdit = async (e) => {
    e?.preventDefault?.();
    if (!editDoc) return;
    try {
      await updateCompanyDoc(companyId, editDoc.id, editDoc);
      setEditDoc(null);
      await refetch();
      notify("Document updated successfully");
    } catch (err) {
      notify(err?.message || "Failed to update document");
    }
  };

  const removeDoc = async (docId) => {
    if (!confirm("Delete document?")) return;
    try {
      await deleteCompanyDoc(companyId, docId);
      await refetch();
      notify("Document deleted successfully");
    } catch (err) {
      notify(err?.message || "Failed to delete document");
    }
  };

  const onChangeIssuedNew = (issued) => {
    const exp = autoExpiry(newDoc.kind, issued);
    setNewDoc((p) => ({ ...p, issued_on: issued, expires_on: exp || p.expires_on }));
  };

  const onChangeIssuedEdit = (issued) => {
    const exp = autoExpiry(editDoc.kind, issued);
    setEditDoc((p) => ({ ...p, issued_on: issued, expires_on: exp || p.expires_on }));
  };

  const onChangeKindNew = (k) => {
    const exp = autoExpiry(k, newDoc.issued_on);
    setNewDoc((p) => ({
      ...p,
      kind: k,
      kind_label: "",
      expires_on: exp || p.expires_on,
      issued_on: "",
      period_from: "",
      period_to: "",
    }));

    if (k === "custom") {
      setShowCustomInput(true);
      setCustomSuggestions(getRecentCustomDocs());
    } else {
      setShowCustomInput(false);
    }
  };

  const onChangeKindEdit = (k) => {
    const exp = autoExpiry(k, editDoc.issued_on);
    setEditDoc((p) => ({ ...p, kind: k, expires_on: exp || p.expires_on }));
  };

  const handleCustomLabelChange = (value) => {
    setNewDoc((prev) => ({ ...prev, kind_label: value }));

    if (value.length >= 2) {
      const recent = getRecentCustomDocs();
      const filtered = recent.filter((d) => d.toLowerCase().includes(value.toLowerCase()));
      setCustomSuggestions(filtered);
    } else {
      setCustomSuggestions(getRecentCustomDocs());
    }
  };

  const selectCustomSuggestion = (label) => {
    setNewDoc((prev) => ({ ...prev, kind_label: label }));
    setCustomSuggestions([]);
  };

  const scrollToUpload = () => {
    if (uploadFormRef.current) {
      uploadFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setTimeout(() => {
        const firstInput = uploadFormRef.current.querySelector("input, select");
        if (firstInput) firstInput.focus();
      }, 500);
    }
  };

  /**
   * ✅ Build dropdown data:
   * - Recommended group always shown
   * - All categories shown only when showAllKinds=true
   * - Always respects scope target (company vs ubo)
   */
  const dropdownData = useMemo(() => {
    const scope = newDoc.scope;

    const scopeAllowedKinds = (DOC_KINDS || []).filter((d) => kindMatchesScopeTarget(d, scope)).map((d) => d.key);

    const recommended = (DOC_KINDS || [])
      .filter((d) => scopeAllowedKinds.includes(d.key))
      .filter((d) => RECOMMENDED_KIND_KEYS.includes(d.key))
      .map((d) => ({ key: d.key, label: d.label, category: d.category, target: d.target }));

    // Make sure custom is present for all scopes (DOC_KINDS has target "both")
    // But if someone removed it later, we still won't crash.
    const hasCustom = recommended.some((d) => d.key === "custom");
    const safeRecommended = hasCustom ? recommended : [...recommended, { key: "custom", label: "Custom Document", category: "custom", target: "both" }];

    const allByCategoryRaw = getDocKindsByCategory?.() || {};
    const allByCategory = {};
    Object.entries(allByCategoryRaw).forEach(([cat, kinds]) => {
      const filtered = (kinds || []).filter((d) => scopeAllowedKinds.includes(d.key));
      if (filtered.length) allByCategory[cat] = filtered;
    });

    return {
      recommended: safeRecommended,
      allByCategory,
      scopeAllowedKinds,
    };
  }, [newDoc.scope]);

  return (
    <>
      {/* Waiver Modal */}
      {waiverModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}>
            <h2 style={{ margin: "0 0 16px 0", fontSize: "20px", fontWeight: "600" }}>Mark Document as Not Applicable</h2>
            <p style={{ margin: "0 0 24px 0", fontSize: "14px", color: "#6b7280" }}>
              You are about to mark <strong>{waiverModal.label}</strong> as not applicable for this company.
            </p>

            <form onSubmit={handleWaiverSubmit}>
              <label style={{ display: "block", marginBottom: "24px" }}>
                <span style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "500" }}>Reason (required) *</span>
                <textarea
                  value={waiverReason}
                  onChange={(e) => setWaiverReason(e.target.value)}
                  placeholder="Why doesn't this requirement apply? (e.g., 'Entity does not have a bank account')"
                  required
                  minLength={10}
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: "14px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    resize: "vertical",
                  }}
                />
                <span style={{ display: "block", marginTop: "4px", fontSize: "12px", color: "#6b7280" }}>Minimum 10 characters</span>
              </label>

              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => {
                    setWaiverModal(null);
                    setWaiverReason("");
                  }}
                  disabled={waiverLoading}
                  style={{
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    color: "#374151",
                    cursor: waiverLoading ? "not-allowed" : "pointer",
                  }}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={waiverLoading || waiverReason.trim().length < 10}
                  style={{
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: waiverLoading || waiverReason.trim().length < 10 ? "#9ca3af" : "#2563eb",
                    color: "white",
                    cursor: waiverLoading || waiverReason.trim().length < 10 ? "not-allowed" : "pointer",
                  }}>
                  {waiverLoading ? "Saving..." : "Confirm Waiver"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Requirements Status Card */}
      <ErrorBoundary componentName="RequirementsStatusCard">
        <RequirementsStatusCard
          requirements={requirements}
          docs={docs}
          waivers={waivers}
          onScrollToUpload={scrollToUpload}
          onWaiverRequest={handleWaiverRequest}
          onWaiverRemove={handleWaiverDelete}
        />
      </ErrorBoundary>

      {/* Documents Table */}
      <div className="card table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Scope</th>
              <th>Kind</th>
              <th>Open</th>
              <th>Number</th>
              <th>Issued</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(docs || []).map((doc) => {
              const expired = (() => {
                if (!doc.expires_on) return false;
                const exp = new Date(doc.expires_on);
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                return exp < sixMonthsAgo;
              })();
              const editing = editDoc && editDoc.id === doc.id;

              return (
                <Fragment key={`doc-${doc.id}`}>
                  <tr className={`${expired ? "row-expired" : ""} ${editing ? "row--editing" : ""}`}>
                    <td>{doc.person_id ? `UBO ${doc.person_name || doc.person_id}` : "Company"}</td>
                    <td>{prettyKind(doc.kind, doc.identifier)}</td>
                    <td>
                      {doc.path ? (
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={async () => {
                            try {
                              await openDocumentWithSignedUrl(() => getCompanyDocSignedUrl(companyId, doc.id), {
                                notify,
                                fallbackDownload: () => downloadCompanyDoc(companyId, doc.id, { admin: true }),
                              });
                            } catch (e) {
                              notify(e?.message || "Could not open document");
                            }
                          }}>
                          {String(doc.path).toLowerCase().endsWith(".pdf") ? "Open PDF" : "Open"}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{doc.identifier || "—"}</td>
                    <td>{doc.issued_on?.slice(0, 10) || "—"}</td>
                    <td>{doc.expires_on?.slice(0, 10) || "—"}</td>
                    <td>{doc.status || "—"}</td>
                    <td className="cell-ellipsis">{doc.note || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {!editing && (
                        <>
                          <button type="button" onClick={() => startEditDocRow(doc)}>
                            Edit
                          </button>
                          <button type="button" className="secondary" onClick={() => removeDoc(doc.id)} style={{ marginLeft: 4 }}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {editing && (
                    <tr className="inline-editor-row">
                      <td colSpan={9}>
                        <form onSubmit={saveDocEdit}>
                          <div className="inline-editor">
                            <label>
                              <span>Kind</span>
                              <select value={editDoc.kind} onChange={(e) => onChangeKindEdit(e.target.value)} required>
                                <option value="">— Select kind —</option>

                                {/* Recommended first */}
                                <optgroup label="Recommended">
                                  {dropdownData.recommended.map((k) => (
                                    <option key={k.key} value={k.key}>
                                      {k.label}
                                    </option>
                                  ))}
                                </optgroup>

                                {/* Full list if enabled */}
                                {showAllKinds &&
                                  Object.entries(dropdownData.allByCategory).map(([category, kinds]) => (
                                    <optgroup key={category} label={titleCase(category)}>
                                      {kinds.map((k) => (
                                        <option key={k.key} value={k.key}>
                                          {k.label}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                              </select>
                            </label>

                            <label className="span-2">
                              <span>File</span>
                              <div className="file-line">
                                {editDoc.path && (
                                  <span className="file-chip" title={String(editDoc.path).split("/").pop()}>
                                    Current
                                  </span>
                                )}
                                <input type="file" accept="image/*,application/pdf" onChange={(e) => setEditDoc({ ...editDoc, file: e.target.files?.[0] || null })} />
                              </div>
                            </label>

                            <label>
                              <span>Number</span>
                              <input value={editDoc.identifier || ""} onChange={(e) => setEditDoc({ ...editDoc, identifier: e.target.value })} />
                            </label>

                            <label>
                              <span>Issued</span>
                              <input type="date" value={editDoc.issued_on || ""} onChange={(e) => onChangeIssuedEdit(e.target.value)} />
                            </label>

                            <label>
                              <span>Expires</span>
                              <input type="date" value={editDoc.expires_on || ""} onChange={(e) => setEditDoc({ ...editDoc, expires_on: e.target.value })} />
                            </label>

                            <label>
                              <span>Status</span>
                              <select value={editDoc.status || ""} onChange={(e) => setEditDoc({ ...editDoc, status: e.target.value })}>
                                <option value="">—</option>
                                {(meta?.docStatuses || []).map((st) => (
                                  <option key={st} value={st}>
                                    {st}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label className="span-2">
                              <span>Note</span>
                              <input value={editDoc.note || ""} onChange={(e) => setEditDoc({ ...editDoc, note: e.target.value })} />
                            </label>
                          </div>

                          <div className="inline-editor__actions">
                            <button type="submit">Save</button>
                            <button type="button" className="secondary" onClick={() => setEditDoc(null)}>
                              Cancel
                            </button>

                            <button type="button" className="btn btn-link" onClick={() => setShowAllKinds((v) => !v)} style={{ marginLeft: "auto" }}>
                              {showAllKinds ? "Hide advanced types" : "Show all document types"}
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}

            {docs.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center">
                  No documents yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Upload Form */}
      <form className="card form-section" onSubmit={handleAddDoc} ref={uploadFormRef}>
        <h3>Add document</h3>

        <div className="form-grid">
          <Field label="Scope" required>
            <div className="radio-group">
              <label className="radio-pill">
                <input
                  type="radio"
                  name="doc_scope"
                  value="company"
                  checked={newDoc.scope === "company"}
                  onChange={() => {
                    setNewDoc({ ...newDoc, scope: "company", person_id: "", kind: "" });
                    setShowCustomInput(false);
                  }}
                />
                <span>Company</span>
              </label>

              <label className="radio-pill">
                <input
                  type="radio"
                  name="doc_scope"
                  value="ubo"
                  checked={newDoc.scope === "ubo"}
                  onChange={() => {
                    setNewDoc({ ...newDoc, scope: "ubo", kind: "" });
                    setShowCustomInput(false);
                  }}
                />
                <span>UBO</span>
              </label>
            </div>
          </Field>

          {newDoc.scope === "ubo" && (
            <Field label="UBO (person)" required error={errors.person_id}>
              <select value={newDoc.person_id || ""} onChange={(e) => setNewDoc({ ...newDoc, person_id: e.target.value })} required>
                <option value="">— Select UBO —</option>
                {uboOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || `Person #${p.id}`}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Kind" required error={errors.kind}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <select value={newDoc.kind} onChange={(e) => onChangeKindNew(e.target.value)} required>
                <option value="">— Select kind —</option>

                {/* Recommended (always shown) */}
                <optgroup label="Recommended">
                  {dropdownData.recommended.map((k) => (
                    <option key={k.key} value={k.key}>
                      {k.label}
                    </option>
                  ))}
                </optgroup>

                {/* All categories (only when enabled) */}
                {showAllKinds &&
                  Object.entries(dropdownData.allByCategory).map(([category, kinds]) => (
                    <optgroup key={category} label={titleCase(category)}>
                      {kinds.map((k) => (
                        <option key={k.key} value={k.key}>
                          {k.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
              </select>

              <button type="button" className="btn btn-link" onClick={() => setShowAllKinds((v) => !v)} style={{ alignSelf: "flex-start" }}>
                {showAllKinds ? "Hide advanced types" : "Show all document types"}
              </button>
            </div>
          </Field>

          {showCustomInput && (
            <Field label="Document name" required error={errors.kind_label}>
              <div style={{ position: "relative" }}>
                <input value={newDoc.kind_label} onChange={(e) => handleCustomLabelChange(e.target.value)} placeholder="e.g., Service Request" required maxLength={100} />
                {customSuggestions.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "#fff",
                      border: "1px solid #dadce0",
                      borderRadius: 4,
                      marginTop: 4,
                      maxHeight: 200,
                      overflowY: "auto",
                      zIndex: 100,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}>
                    {customSuggestions.map((suggestion, idx) => (
                      <div
                        key={`${suggestion}-${idx}`}
                        onClick={() => selectCustomSuggestion(suggestion)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontSize: 14,
                          borderBottom: idx < customSuggestions.length - 1 ? "1px solid #f1f3f4" : "none",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}>
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Field>
          )}

          <Field label="File">
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files?.[0] || null })} />
          </Field>

          {visibleFields.shows.identifier && (
            <Field label="Identifier / number" required={visibleFields.required.includes("identifier")}>
              <input value={newDoc.identifier} onChange={(e) => setNewDoc({ ...newDoc, identifier: e.target.value })} />
            </Field>
          )}

          {visibleFields.shows.issued_on && (
            <Field label="Issued on" required={visibleFields.required.includes("issued_on")}>
              <input type="date" value={newDoc.issued_on} onChange={(e) => onChangeIssuedNew(e.target.value)} />
            </Field>
          )}

          {visibleFields.shows.expires_on && (
            <Field label="Expires on" required={visibleFields.required.includes("expires_on")}>
              <input type="date" value={newDoc.expires_on} onChange={(e) => setNewDoc({ ...newDoc, expires_on: e.target.value })} />
            </Field>
          )}

          {visibleFields.shows.period_from && (
            <Field label="Period from" required={visibleFields.required.includes("period_from")}>
              <input type="date" value={newDoc.period_from} onChange={(e) => setNewDoc({ ...newDoc, period_from: e.target.value })} />
            </Field>
          )}

          {visibleFields.shows.period_to && (
            <Field label="Period to" required={visibleFields.required.includes("period_to")}>
              <input type="date" value={newDoc.period_to} onChange={(e) => setNewDoc({ ...newDoc, period_to: e.target.value })} />
            </Field>
          )}

          {visibleFields.shows.authority && (
            <Field label="Issuing authority" required={visibleFields.required.includes("authority")}>
              <input value={newDoc.authority} onChange={(e) => setNewDoc({ ...newDoc, authority: e.target.value })} placeholder="e.g., Government of Curaçao" />
            </Field>
          )}

          <Field label="Note">
            <input value={newDoc.note} onChange={(e) => setNewDoc({ ...newDoc, note: e.target.value })} />
          </Field>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? "Adding..." : "Add document"}
          </button>
        </div>
      </form>
    </>
  );
};
